/**
 * App-wide toast notifications.
 *
 * One provider, one viewport. The viewport is rendered by StatusStack
 * (AppLayout) in the same fixed column as the OfflineIndicator, offset above
 * the mobile bottom nav via --onsite-bottom-offset, so toasts and the sync
 * indicator never overlap each other or the navigation.
 *
 * Every mutation in the app reports through `notify()`; success and info
 * toasts announce politely, failures use role="alert" and stay longer.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type ToastTone = 'success' | 'error' | 'info' | 'warning';

export interface ToastOptions {
  tone?: ToastTone;
  /** Milliseconds before auto-dismiss. Errors default to 8s, everything else 4s. */
  durationMs?: number;
}

interface ToastItem {
  id: number;
  message: string;
  tone: ToastTone;
  durationMs: number;
}

interface ToastContextValue {
  notify: (message: string, options?: ToastOptions | ToastTone) => void;
  dismiss: (id: number) => void;
  toasts: ToastItem[];
}

const ToastContext = createContext<ToastContextValue | null>(null);

const MAX_VISIBLE = 3;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const notify = useCallback((message: string, options?: ToastOptions | ToastTone) => {
    const opts: ToastOptions = typeof options === 'string' ? { tone: options } : options ?? {};
    const tone = opts.tone ?? 'info';
    const durationMs = opts.durationMs ?? (tone === 'error' ? 8000 : 4000);
    const id = nextId.current++;
    setToasts((list) => [...list, { id, message, tone, durationMs }].slice(-MAX_VISIBLE));
  }, []);

  const value = useMemo(() => ({ notify, dismiss, toasts }), [notify, dismiss, toasts]);

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

export function useToast(): Pick<ToastContextValue, 'notify' | 'dismiss'> {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return { notify: ctx.notify, dismiss: ctx.dismiss };
}

const TONE_CLASSES: Record<ToastTone, string> = {
  success: 'border-success/40 bg-success-soft text-success',
  error: 'border-error/40 bg-error-soft text-error',
  warning: 'border-warning/40 bg-warning-soft text-warning',
  info: 'border-border bg-surface-strong text-text',
};

function ToastCard({ toast, dismiss }: { toast: ToastItem; dismiss: (id: number) => void }) {
  // `dismiss` is stable (useCallback in the provider), so the timer is set
  // once per toast and is not reset when sibling toasts come and go.
  useEffect(() => {
    const timer = window.setTimeout(() => dismiss(toast.id), toast.durationMs);
    return () => window.clearTimeout(timer);
  }, [toast.id, toast.durationMs, dismiss]);

  const onDismiss = () => dismiss(toast.id);
  const isAlert = toast.tone === 'error';

  return (
    <div
      role={isAlert ? 'alert' : 'status'}
      className={`pointer-events-auto flex w-full max-w-md items-start gap-2 rounded-lg border px-3 py-2 text-sm shadow-lg ${TONE_CLASSES[toast.tone]}`}
    >
      <p className="min-w-0 flex-1 break-words py-2 leading-snug">{toast.message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss notification"
        className="-mr-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-md opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-current"
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

/** Renders the queued toasts. Place inside the shared bottom status column. */
export function ToastViewport() {
  const ctx = useContext(ToastContext);
  if (!ctx || ctx.toasts.length === 0) return null;
  return (
    <div className="flex w-full flex-col items-center gap-2">
      {ctx.toasts.map((toast) => (
        <ToastCard key={toast.id} toast={toast} dismiss={ctx.dismiss} />
      ))}
    </div>
  );
}
